import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  deleteDoc,
  collection, 
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  QUESTION_SECTIONS, 
  SECTION_ORDER, 
  getNextUnanswered, 
  getCompletionPercentage,
  getTotalQuestionCount,
  getAllQuestions
} from '../data/questions';

const LifeStoryContext = createContext(null);

export function LifeStoryProvider({ children }) {
  // Support both standalone (/story/:storyId) and legacy (/community/:communityId/resident/:residentId) routes
  const { storyId, communityId, residentId } = useParams();
  const { user } = useAuth();
  
  // Determine if we're in standalone mode
  const isStandalone = !!storyId;
  
  // Core state
  const [lifeStory, setLifeStory] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Current question state
  const [currentQuestion, setCurrentQuestion] = useState(null);

  // Community question bank — overrides defaults when present
  const [communityQuestionBank, setCommunityQuestionBank] = useState(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  // Active questions: community-customized or defaults
  const activeSections = useMemo(() => {
    if (communityQuestionBank?.sections) {
      return communityQuestionBank.sections;
    }
    return QUESTION_SECTIONS;
  }, [communityQuestionBank]);

  const activeSectionOrder = useMemo(() => {
    if (communityQuestionBank?.sectionOrder) {
      return communityQuestionBank.sectionOrder;
    }
    return SECTION_ORDER;
  }, [communityQuestionBank]);

  // Dynamic question helpers that respect community customizations
  const activeGetNextUnanswered = useCallback((answeredIds = [], skippedSects = []) => {
    for (const sectionId of activeSectionOrder) {
      if (skippedSects.includes(sectionId)) continue;
      const section = activeSections[sectionId];
      if (!section) continue;
      for (const question of section.questions) {
        if (!answeredIds.includes(question.id)) {
          return { ...question, section: sectionId };
        }
      }
    }
    return null;
  }, [activeSections, activeSectionOrder]);

  const activeGetTotalQuestionCount = useCallback(() => {
    return Object.values(activeSections)
      .filter(section => section.id !== 'consent')
      .reduce((total, section) => total + section.questions.length, 0);
  }, [activeSections]);

  const activeGetCompletionPercentage = useCallback((answeredIds = []) => {
    const totalQuestions = activeGetTotalQuestionCount();
    const answeredCount = answeredIds.filter(id => !id.startsWith('consent_') && id !== 'intro_1').length;
    return totalQuestions > 0 ? Math.min(100, Math.round((answeredCount / totalQuestions) * 100)) : 0;
  }, [activeGetTotalQuestionCount]);

  const activeGetAllQuestions = useCallback(() => {
    const questions = [];
    for (const sectionId of activeSectionOrder) {
      const section = activeSections[sectionId];
      if (!section) continue;
      for (const question of section.questions) {
        questions.push({ ...question, section: sectionId });
      }
    }
    return questions;
  }, [activeSections, activeSectionOrder]);

  // ============================================
  // Get document references based on mode
  // ============================================
  
  const getLifeStoryRef = useCallback(() => {
    if (isStandalone) {
      return doc(db, 'lifeStories', storyId);
    } else {
      return doc(db, 'communities', communityId, 'residents', residentId, 'lifeStory', 'main');
    }
  }, [isStandalone, storyId, communityId, residentId]);
  
  const getResponsesRef = useCallback(() => {
    if (isStandalone) {
      return collection(db, 'lifeStories', storyId, 'responses');
    } else {
      return collection(db, 'communities', communityId, 'residents', residentId, 'lifeStory', 'main', 'responses');
    }
  }, [isStandalone, storyId, communityId, residentId]);
  
  const getStoragePath = useCallback((filename) => {
    if (isStandalone) {
      return `life-stories/${storyId}/audio/${filename}`;
    } else {
      return `communities/${communityId}/life-stories/${residentId}/audio/${filename}`;
    }
  }, [isStandalone, storyId, communityId, residentId]);

  // ============================================
  // Load community question bank (if applicable)
  // ============================================

  useEffect(() => {
    if (!lifeStory?.communityId) return;

    const loadQuestions = async () => {
      setQuestionsLoading(true);
      try {
        const functions = getFunctions();
        const loadFn = httpsCallable(functions, 'loadCommunityQuestions');
        const result = await loadFn({ communityId: lifeStory.communityId });

        if (result.data.success && result.data.isCustomized) {
          setCommunityQuestionBank(result.data.questionBank);
        }
      } catch (err) {
        console.error('Error loading community questions, using defaults:', err);
        // Silently fall back to defaults
      } finally {
        setQuestionsLoading(false);
      }
    };

    loadQuestions();
  }, [lifeStory?.communityId]);

  // ============================================
  // Load/subscribe to life story
  // ============================================
  
  useEffect(() => {
    if (isStandalone && !storyId) return;
    if (!isStandalone && (!communityId || !residentId)) return;
    
    const lifeStoryRef = getLifeStoryRef();
    
    const unsubscribe = onSnapshot(lifeStoryRef, (snapshot) => {
      if (snapshot.exists()) {
        setLifeStory({ id: snapshot.id, ...snapshot.data() });
      } else {
        setLifeStory(null);
        if (!isStandalone) {
          setError('Life story not found');
        }
      }
      setLoading(false);
    }, (err) => {
      console.error('Error subscribing to life story:', err);
      setError('Failed to load life story');
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [isStandalone, storyId, communityId, residentId, getLifeStoryRef]);

  // ============================================
  // Subscribe to responses
  // ============================================
  
  useEffect(() => {
    if (isStandalone && !storyId) return;
    if (!isStandalone && (!communityId || !residentId)) return;
    
    const responsesRef = getResponsesRef();
    
    const unsubscribe = onSnapshot(responsesRef, (snapshot) => {
      const responsesMap = {};
      snapshot.docs.forEach(doc => {
        responsesMap[doc.id] = { id: doc.id, ...doc.data() };
      });
      setResponses(responsesMap);
    });
    
    return () => unsubscribe();
  }, [isStandalone, storyId, communityId, residentId, getResponsesRef]);

  // ============================================
  // Track skipped sections (e.g., children if no kids)
  // ============================================
  
  const [skippedSections, setSkippedSections] = useState([]);
  const [initialQuestionSet, setInitialQuestionSet] = useState(false);

  // Load skipped sections from Firestore
  useEffect(() => {
    if (lifeStory?.skippedSections) {
      setSkippedSections(lifeStory.skippedSections);
    }
  }, [lifeStory?.skippedSections]);

  // ============================================
  // Set current question based on progress (ONLY on initial load)
  // ============================================
  
  useEffect(() => {
    // Only set the initial question once when the story loads
    if (initialQuestionSet) return;
    if (!lifeStory) return;
    
    const answeredIds = Object.keys(responses);
    
    if (!lifeStory?.consent) {
      // Start with consent questions
      setCurrentQuestion({
        ...activeSections.consent.questions[0],
        section: 'consent'
      });
    } else {
      // Find next unanswered question (respecting skipped sections)
      const skipped = lifeStory.skippedSections || [];
      const next = activeGetNextUnanswered(answeredIds, skipped);
      setCurrentQuestion(next);
    }
    
    setInitialQuestionSet(true);
  }, [lifeStory, responses, initialQuestionSet]);

  // ============================================
  // Record consent (verbal or POA)
  // ============================================
  
  const recordConsent = useCallback(async (consentData) => {
    if (!user) return;
    
    const lifeStoryRef = getLifeStoryRef();
    
    try {
      await updateDoc(lifeStoryRef, {
        consent: {
          ...consentData,
          consentedAt: serverTimestamp(),
          consentRecordedBy: {
            uid: user.uid,
            name: user.displayName
          }
        },
        preferredName: consentData.preferredName || null,
        status: 'in_progress',
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error recording consent:', err);
      throw err;
    }
  }, [user, getLifeStoryRef]);

  // ============================================
  // Save response
  // ============================================
  
  const saveResponse = useCallback(async (questionId, section, audioBlob, privacy = null) => {
    if (!user || !audioBlob) {
      throw new Error('Missing required data');
    }
    
    try {
      // 1. Upload audio to Firebase Storage
      const timestamp = Date.now();
      const extension = audioBlob.type.includes('mp4') ? 'm4a' : 'webm';
      const filename = `${questionId}_${timestamp}.${extension}`;
      const storagePath = getStoragePath(filename);
      
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, audioBlob);
      
      // 2. Create response document
      const responsesRef = getResponsesRef();
      const responseRef = doc(responsesRef, questionId);
      
      const defaultPrivacy = 'private'; // Default to private for standalone stories
      
      await setDoc(responseRef, {
        questionId,
        section,
        responseType: 'resident',
        audioUrl: storagePath,
        audioDuration: 0,
        transcript: null,
        transcriptStatus: 'pending',
        transcriptCompletedAt: null,
        recordedAt: serverTimestamp(),
        recordedBy: {
          uid: user.uid,
          name: user.displayName,
          relationship: user.role || 'family'
        },
        photos: [],
        privacy: privacy || defaultPrivacy
      });
      
      // 3. Update life story document
      const lifeStoryRef = getLifeStoryRef();
      const answeredIds = [...Object.keys(responses), questionId];
      const completionPercentage = activeGetCompletionPercentage(answeredIds);
      
      await updateDoc(lifeStoryRef, {
        updatedAt: serverTimestamp(),
        completionPercentage
      });
      
      return true;
    } catch (err) {
      console.error('Error saving response:', err);
      throw err;
    }
  }, [user, responses, getStoragePath, getResponsesRef, getLifeStoryRef]);

  // ============================================
  // Navigation
  // ============================================
  
  const goToQuestion = useCallback((questionId) => {
    for (const section of Object.values(activeSections)) {
      const question = section.questions.find(q => q.id === questionId);
      if (question) {
        setCurrentQuestion({ ...question, section: section.id });
        return;
      }
    }
  }, [activeSections]);

  const goToSection = useCallback((sectionId) => {
    const section = activeSections[sectionId];
    if (section && section.questions.length > 0) {
      setCurrentQuestion({
        ...section.questions[0],
        section: sectionId
      });
    }
  }, [activeSections]);

  const skipQuestion = useCallback(() => {
    if (!currentQuestion) return;
    
    const section = activeSections[currentQuestion.section];
    const currentIndex = section.questions.findIndex(q => q.id === currentQuestion.id);
    
    // Try next question in section
    if (currentIndex < section.questions.length - 1) {
      setCurrentQuestion({
        ...section.questions[currentIndex + 1],
        section: currentQuestion.section
      });
      return;
    }
    
    // Move to next section (skipping over skipped sections)
    const sectionIndex = activeSectionOrder.indexOf(currentQuestion.section);
    for (let i = sectionIndex + 1; i < activeSectionOrder.length; i++) {
      const nextSectionId = activeSectionOrder[i];
      // Skip over sections that are in skippedSections
      if (skippedSections.includes(nextSectionId)) continue;
      
      const nextSection = activeSections[nextSectionId];
      if (nextSection && nextSection.questions.length > 0) {
        setCurrentQuestion({
          ...nextSection.questions[0],
          section: nextSectionId
        });
        return;
      }
    }
    
    // All done
    setCurrentQuestion(null);
  }, [currentQuestion, skippedSections, activeSections, activeSectionOrder]);

  // Skip an entire section (e.g., children if no kids)
  const skipSection = useCallback(async (sectionId) => {
    if (!user) return;
    
    const newSkippedSections = [...skippedSections, sectionId];
    setSkippedSections(newSkippedSections);
    
    // Save to Firestore
    const lifeStoryRef = getLifeStoryRef();
    await updateDoc(lifeStoryRef, {
      skippedSections: newSkippedSections,
      updatedAt: serverTimestamp()
    });
    
    // Move to next non-skipped section
    const currentSectionIndex = activeSectionOrder.indexOf(sectionId);
    for (let i = currentSectionIndex + 1; i < activeSectionOrder.length; i++) {
      const nextSectionId = activeSectionOrder[i];
      if (newSkippedSections.includes(nextSectionId)) continue;
      
      const nextSection = activeSections[nextSectionId];
      if (nextSection && nextSection.questions.length > 0) {
        setCurrentQuestion({
          ...nextSection.questions[0],
          section: nextSectionId
        });
        return;
      }
    }
    
    // All done
    setCurrentQuestion(null);
  }, [user, skippedSections, getLifeStoryRef, activeSections, activeSectionOrder]);

  // Unskip a section
  const unskipSection = useCallback(async (sectionId) => {
    if (!user) return;
    
    const newSkippedSections = skippedSections.filter(s => s !== sectionId);
    setSkippedSections(newSkippedSections);
    
    // Save to Firestore
    const lifeStoryRef = getLifeStoryRef();
    await updateDoc(lifeStoryRef, {
      skippedSections: newSkippedSections,
      updatedAt: serverTimestamp()
    });
  }, [user, skippedSections, getLifeStoryRef]);

  const nextQuestion = useCallback(() => {
    const answeredIds = Object.keys(responses);
    const next = activeGetNextUnanswered([...answeredIds, currentQuestion?.id].filter(Boolean), skippedSections);
    setCurrentQuestion(next);
  }, [responses, currentQuestion, skippedSections]);

  // Navigate to next question (regardless of answered status)
  const goToNext = useCallback(() => {
    if (!currentQuestion) return;
    
    const allQuestions = activeGetAllQuestions();
    const currentIndex = allQuestions.findIndex(q => q.id === currentQuestion.id);
    
    // Find next question that's not in a skipped section
    for (let i = currentIndex + 1; i < allQuestions.length; i++) {
      if (!skippedSections.includes(allQuestions[i].section)) {
        setCurrentQuestion(allQuestions[i]);
        return;
      }
    }
    
    // No more questions
    setCurrentQuestion(null);
  }, [currentQuestion, skippedSections]);

  // Navigate to previous question
  const goToPrevious = useCallback(() => {
    if (!currentQuestion) return;
    
    const allQuestions = activeGetAllQuestions();
    const currentIndex = allQuestions.findIndex(q => q.id === currentQuestion.id);
    
    // Find previous question that's not in a skipped section
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!skippedSections.includes(allQuestions[i].section)) {
        setCurrentQuestion(allQuestions[i]);
        return;
      }
    }
  }, [currentQuestion, skippedSections]);

  // Delete a response
  const deleteResponse = useCallback(async (questionId) => {
    if (!user) return;
    
    try {
      const responsesRef = getResponsesRef();
      const responseRef = doc(responsesRef, questionId);
      
      // Get the response to find the audio URL
      const responseDoc = await getDoc(responseRef);
      if (responseDoc.exists()) {
        const responseData = responseDoc.data();
        
        // Delete audio from storage
        if (responseData.audioUrl) {
          try {
            const audioRef = ref(storage, responseData.audioUrl);
            await deleteObject(audioRef);
          } catch (err) {
            console.warn('Could not delete audio file:', err);
          }
        }
        
        // Delete additional recordings if present
        if (responseData.additionalRecordings) {
          for (const rec of responseData.additionalRecordings) {
            try {
              const audioRef = ref(storage, rec.audioUrl);
              await deleteObject(audioRef);
            } catch (err) {
              console.warn('Could not delete additional audio:', err);
            }
          }
        }
      }
      
      // Delete the response document
      await deleteDoc(responseRef);
      
      // Update completion percentage
      const lifeStoryRef = getLifeStoryRef();
      const answeredIds = Object.keys(responses).filter(id => id !== questionId);
      const completionPercentage = activeGetCompletionPercentage(answeredIds);
      
      await updateDoc(lifeStoryRef, {
        updatedAt: serverTimestamp(),
        completionPercentage
      });
      
      return true;
    } catch (err) {
      console.error('Error deleting response:', err);
      throw err;
    }
  }, [user, responses, getResponsesRef, getLifeStoryRef]);

  // Add additional recording to existing response
  const addRecording = useCallback(async (questionId, audioBlob) => {
    if (!user || !audioBlob) {
      throw new Error('Missing required data');
    }
    
    try {
      // Upload audio
      const timestamp = Date.now();
      const extension = audioBlob.type.includes('mp4') ? 'm4a' : 'webm';
      const filename = `${questionId}_add_${timestamp}.${extension}`;
      const storagePath = getStoragePath(filename);
      
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, audioBlob);
      
      // Get existing response
      const responsesRef = getResponsesRef();
      const responseRef = doc(responsesRef, questionId);
      const responseDoc = await getDoc(responseRef);
      
      if (!responseDoc.exists()) {
        throw new Error('Response not found');
      }
      
      const existingData = responseDoc.data();
      const additionalRecordings = existingData.additionalRecordings || [];
      
      // Add new recording
      additionalRecordings.push({
        audioUrl: storagePath,
        recordedAt: new Date().toISOString(),
        recordedBy: {
          uid: user.uid,
          name: user.displayName
        }
      });
      
      await updateDoc(responseRef, {
        additionalRecordings,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (err) {
      console.error('Error adding recording:', err);
      throw err;
    }
  }, [user, getStoragePath, getResponsesRef]);

  // ============================================
  // Progress calculations
  // ============================================
  
  const getProgress = useCallback(() => {
    const answeredIds = Object.keys(responses);
    
    return {
      completionPercentage: activeGetCompletionPercentage(answeredIds),
      answeredCount: answeredIds.filter(id => !id.startsWith('consent_') && id !== 'intro_1').length,
      totalQuestions: activeGetTotalQuestionCount(),
      hasConsent: !!lifeStory?.consent
    };
  }, [responses, lifeStory]);

  const getSectionProgress = useCallback(() => {
    const answeredIds = Object.keys(responses);
    
    return activeSectionOrder
      .filter(id => id !== 'consent')
      .map(sectionId => {
        const section = activeSections[sectionId];
        const sectionAnswered = section.questions.filter(q => answeredIds.includes(q.id));
        
        return {
          sectionId,
          title: section.title,
          icon: section.icon,
          description: section.description,
          totalQuestions: section.questions.length,
          answeredQuestions: sectionAnswered.length,
          isComplete: sectionAnswered.length >= section.questions.length,
          optional: section.optional || false,
          conditional: section.conditional || false
        };
      });
  }, [responses, activeSections, activeSectionOrder]);

  // ============================================
  // Resident name helper
  // ============================================
  
  const residentName = lifeStory?.preferredName || 
                       lifeStory?.residentName || 
                       'the resident';

  // ============================================
  // Context value
  // ============================================
  
  const value = {
    // Data
    lifeStory,
    responses,
    currentQuestion,
    residentName,
    
    // State
    loading,
    error,
    hasConsent: !!lifeStory?.consent,
    isStandalone,
    
    // Actions
    recordConsent,
    saveResponse,
    deleteResponse,
    addRecording,
    
    // Navigation
    goToQuestion,
    goToSection,
    skipQuestion,
    skipSection,
    unskipSection,
    nextQuestion,
    goToNext,
    goToPrevious,
    skippedSections,
    
    // Progress
    getProgress,
    getSectionProgress,
    
    // Data refs
    storyId,
    communityId,
    residentId,
    sections: activeSections,
    sectionOrder: activeSectionOrder,
    isCustomQuestions: !!communityQuestionBank
  };

  return (
    <LifeStoryContext.Provider value={value}>
      {children}
    </LifeStoryContext.Provider>
  );
}

export function useLifeStory() {
  const context = useContext(LifeStoryContext);
  if (!context) {
    throw new Error('useLifeStory must be used within a LifeStoryProvider');
  }
  return context;
}

export default LifeStoryContext;
